import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAttendanceSessionsForDateQuery,
  useGetTeamQuery,
} from "../../Api/attoDeskApi";
import { getAvatar } from "../../utils/avatar";
import { formatDateOnly, parseDateOnly, presentDateOnly, todayLocal } from "../../utils/dateOnly";
import GradeSubjectsSummary, { GradeSubjectsBucket } from "../AttendanceSession/GradeSubjectsSummary";
import GradeSubjectsDialog, { summarizeGradesCompact } from "../AttendanceSession/GradeSubjectsDialog";
import useUser from "../../Core/hook/useUser";

interface SessionRow {
  id: number;
  class_name: string;
  grade: string | null;
  start_time: string;
  end_time: string;
  date: string;
  location: string | null;
  staff_name: string | null;
  staff_photo: string | null;
  student_count: number;
  subjects: string;
  grade_subjects?: GradeSubjectsBucket[] | null;
  attendance_started: boolean;
  attendance_submitted: boolean;
  is_cancelled: boolean;
  present_count: number;
  absent_count: number;
  late_count: number;
}

type RangeKey = "today" | "week" | "month" | "all";

// Batch 16 — the old `iso = d => d.toISOString().split("T")[0]` was the
// exact toISOString-split bug utils/dateOnly.ts exists to prevent. It
// silently offset "today" (and the This-Week / This-Month boundaries)
// by a day depending on the browser's UTC offset. Replaced with
// formatDateOnly(), which reads local Y/M/D components.
const iso = (d: Date) => formatDateOnly(d);

// Monday-based week window that contains `d`.
const weekWindow = (d: Date) => {
  const day = d.getDay() || 7; // Sun=0 → 7
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: iso(monday), to: iso(sunday) };
};

const monthWindow = (d: Date) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: iso(first), to: iso(last) };
};

const rangeFor = (key: RangeKey): { from?: string; to?: string; date?: string } => {
  const now = new Date();
  if (key === "today") return { date: todayLocal() };
  if (key === "week") return weekWindow(now);
  if (key === "month") return monthWindow(now);
  // "all" — a generous window (90d back, 30d forward)
  const back = new Date(now); back.setDate(now.getDate() - 90);
  const fwd = new Date(now); fwd.setDate(now.getDate() + 30);
  return { from: iso(back), to: iso(fwd) };
};

const formatTime = (t: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const dt = new Date();
  dt.setHours(h, m || 0, 0, 0);
  return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Batch 16 — presentDateOnly() parses YYYY-MM-DD in the LOCAL timezone.
// The old `new Date(isoDate)` treated the string as UTC midnight, which
// west of UTC renders as the previous day (the "Sun Jul 26" bug).
const formatDateShort = (isoDate: string) =>
  presentDateOnly(isoDate, { weekday: "short", month: "short", day: "numeric" });

const AttendanceLanding: React.FC = () => {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState<RangeKey>("week");
  // Tab selector — Upcoming/Today vs Past Records. Past shows a
  // "View Report" CTA; upcoming/today shows "Mark Attendance".
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  const todayStr = iso(new Date());
  // selectedStaff now holds {id, name} so the custom dropdown trigger
  // can render the selected name + avatar without a second lookup.
  // The RTK Query args only depend on `id` so switching between two
  // staff triggers a refetch, but re-renders that change the label
  // don't refetch the sessions.
  // Staff-role lock: teachers must never see or filter to other staff.
  // The dropdown becomes a read-only chip showing the logged-in
  // teacher; args.staff_id is pinned to their staffId. Admins keep
  // the full "All Staff" + per-teacher picker.
  const { isStaffRole, staffId, userData } = useUser();
  const staffDisplayName = (userData?.staff && userData.staff.trim()) || userData?.username || "You";
  const [selectedStaff, setSelectedStaff] = useState<{ id: string | number; name: string }>({ id: "all", name: "All Staff" });
  // Sync selection to the acting teacher whenever we know we're in
  // Staff mode (initial load and post-login role changes).
  useEffect(() => {
    if (isStaffRole && staffId != null) {
      setSelectedStaff((prev) => (prev.id === staffId ? prev : { id: staffId, name: staffDisplayName }));
    }
  }, [isStaffRole, staffId, staffDisplayName]);
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  // Local, in-memory type-ahead against the already-loaded staffList. No
  // extra API call is needed — the full teacher roster is fetched via
  // useGetTeamQuery above. Reset on close so re-opening starts fresh.
  const [staffSearch, setStaffSearch] = useState("");
  useEffect(() => {
    if (!staffDropdownOpen) setStaffSearch("");
  }, [staffDropdownOpen]);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest("#staff-dropdown")) setStaffDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  // Escape closes the dropdown when it's open. Matches the click-outside
  // behaviour so keyboard users get the same exit affordance.
  useEffect(() => {
    if (!staffDropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStaffDropdownOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [staffDropdownOpen]);
  // For Staff-role users, force staff_id to the logged-in teacher id
  // regardless of what's in selectedStaff on the first render — this
  // eliminates the "All Staff" flash before the sync effect runs and
  // matches the backend's teaching-scope clamp.
  const effectiveStaffId = (isStaffRole && staffId != null)
    ? String(staffId)
    : String(selectedStaff.id);
  const args = useMemo(
    () => ({ ...rangeFor(rangeKey), staff_id: effectiveStaffId }),
    [rangeKey, effectiveStaffId]
  );
  const { data, isFetching, refetch } = useGetAttendanceSessionsForDateQuery(args);
  const sessions: SessionRow[] = (data?.data as SessionRow[]) || [];
  // View-Subjects modal state, single instance shared by every card so
  // opening a second card seamlessly swaps content (no unmount flash).
  const [subjectsModal, setSubjectsModal] = useState<null | {
    groups: GradeSubjectsBucket[]; subtitle: string;
  }>(null);

  // Staff dropdown — reuse existing /team/getAll endpoint.
  // The `teams` table columns are `teamFirstName` / `teamLastName`
  // (not `first_name` / `last_name`), so the previous mapping fell
  // through to the "Staff #{id}" fallback for every row. Read the
  // real column names first; keep the generic aliases as a safety net
  // in case a future migration renames them.
  // Skip the /team/getAll fetch entirely for Staff-role users — they
  // never see a per-teacher picker, and Staff RBAC typically doesn't
  // include Team_View anyway (which would 403 the request).
  const { data: teamData } = useGetTeamQuery({ searchText: "" } as any, { skip: isStaffRole });
  const staffList: { id: number; name: string }[] = ((teamData?.data as any[]) || []).map(
    (t: any) => {
      const first = t.teamFirstName ?? t.first_name ?? "";
      const last  = t.teamLastName  ?? t.last_name  ?? "";
      const full  = `${first} ${last}`.trim();
      return {
        id: t.id,
        name: full || t.name || t.full_name || t.teamId || `Staff #${t.id}`,
      };
    }
  );

  const goSession = (id: number) =>
    navigate(`/class-sessions/${id}/attendance?return_url=/attendance`);

  const rangeLabel: Record<RangeKey, string> = {
    today: "Today",
    week: "This Week",
    month: "This Month",
    all: "All",
  };

  return (
    <div
      style={{
        padding: 32, background: "#f0f4f8", minHeight: "100vh",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1b3a6b", margin: 0 }}>
          Mark Attendance
        </h1>
        <p style={{ color: "#94a3b8", marginTop: 4, fontSize: 14 }}>
          Select a session below to mark student attendance
        </p>
      </div>

      {/* Tab strip: Upcoming & Today vs Past Records. Past sessions
          use "View Report" instead of "Mark Attendance". */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 20,
        background: "#fff", borderRadius: 12, padding: 4,
        border: "1px solid #e2e8f0", width: "fit-content",
      }}>
        {([
          { id: "upcoming" as const, label: "📅 Upcoming & Today" },
          { id: "past" as const, label: "📋 Past Records" },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "9px 20px", borderRadius: 9, border: "none",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: activeTab === tab.id ? "#1b3a6b" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b",
              transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Range toggle */}
      <div
        style={{
          background: "#fff", borderRadius: 14, padding: "12px 16px",
          marginBottom: 20, border: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", gap: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)", flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 600, color: "#1b3a6b", fontSize: 14, marginRight: 4 }}>
          📅 View:
        </span>
        {(["today", "week", "month", "all"] as RangeKey[]).map((k) => {
          const active = rangeKey === k;
          return (
            <button
              key={k}
              onClick={() => setRangeKey(k)}
              style={{
                background: active ? "#1b3a6b" : "#f8fafc",
                color: active ? "#fff" : "#64748b",
                border: `1px solid ${active ? "#1b3a6b" : "#e2e8f0"}`,
                borderRadius: 9, padding: "7px 14px",
                fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}
            >
              {rangeLabel[k]}
            </button>
          );
        })}

        {/* Divider */}
        <div style={{ width: 1, height: 32, background: "#e2e8f0", margin: "0 4px" }} />

        {/* Staff filter — custom dropdown so the trigger can show an
            avatar + a real staff-name pill instead of the browser's
            native <select> chrome, and the menu can list initials
            avatars per row. Selection state is object-shaped so the
            trigger can render the current name without another lookup.
            Refetch is automatic: `args` memoizes on selectedStaff.id
            and RTK Query re-runs the sessions query on change. */}
        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
          👨‍🏫 Staff:
        </span>
        {isStaffRole ? (
          // Staff Portal: filter is locked to the logged-in teacher.
          // No dropdown, no "All Staff", no other-teacher picker.
          <div
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 14px",
              background: "#fff",
              border: "1.5px solid #e2e8f0",
              borderRadius: 10,
              minWidth: 200,
              cursor: "default",
            }}
            title="Your sessions only"
          >
            <div
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "#1b3a6b",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, color: "#fff", fontWeight: 700, flexShrink: 0,
              }}
            >
              {staffDisplayName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {staffDisplayName}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Your sessions only</div>
            </div>
            <span style={{ fontSize: 12, color: "#94a3b8" }} aria-hidden>🔒</span>
          </div>
        ) : (
        <div id="staff-dropdown" style={{ position: "relative", minWidth: 200 }}>
          {/* Trigger */}
          <button
            type="button"
            onClick={() => setStaffDropdownOpen((v) => !v)}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 14px",
              background: "#fff",
              border: staffDropdownOpen ? "2px solid #1b3a6b" : "1.5px solid #e2e8f0",
              borderRadius: 10,
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: staffDropdownOpen ? "0 0 0 3px rgba(27,58,107,0.1)" : "none",
            }}
          >
            {/* Selected avatar */}
            <div
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: selectedStaff.id === "all" ? "#e0e7ff" : "#1b3a6b",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: selectedStaff.id === "all" ? 14 : 11,
                color: selectedStaff.id === "all" ? "#4338ca" : "#fff",
                fontWeight: 700, flexShrink: 0,
              }}
            >
              {selectedStaff.id === "all"
                ? "👥"
                : selectedStaff.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            {/* Name */}
            <span
              style={{
                flex: 1, textAlign: "left",
                fontSize: 13, fontWeight: 600,
                color: selectedStaff.id === "all" ? "#64748b" : "#1a1a2e",
              }}
            >
              {selectedStaff.name}
            </span>
            {/* Arrow */}
            <span
              style={{
                fontSize: 11, color: "#94a3b8",
                transform: staffDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              ▼
            </span>
          </button>

          {/* Menu */}
          {staffDropdownOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 6px)",
                left: 0, right: 0, zIndex: 200,
                background: "#fff",
                border: "1.5px solid #e2e8f0",
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                overflow: "hidden",
                // Menu chrome + list share the height budget; the list
                // scrolls internally so the search input stays sticky at
                // the top when the roster is long.
                maxHeight: 360,
                display: "flex", flexDirection: "column",
                animation: "staffDropdownFade 0.15s ease",
              }}
            >
              {/* Sticky search — filters staffList in-memory only, no API call.
                  Autofocus so the user can start typing immediately after
                  clicking the trigger. Escape (handled at the effect level
                  above) closes the whole dropdown. */}
              <div
                style={{
                  position: "sticky", top: 0, zIndex: 1,
                  padding: "10px 12px",
                  background: "#fff",
                  borderBottom: "1px solid #f1f5f9",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                  }}
                >
                  <span style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1 }} aria-hidden="true">🔍</span>
                  <input
                    type="text"
                    autoFocus
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    placeholder="Search staff..."
                    aria-label="Search staff"
                    style={{
                      flex: 1, minWidth: 0,
                      border: "none", outline: "none",
                      background: "transparent",
                      fontSize: 13, color: "#1a1a2e",
                      padding: 0,
                    }}
                  />
                  {staffSearch && (
                    <button
                      onClick={() => setStaffSearch("")}
                      aria-label="Clear search"
                      style={{
                        border: "none", background: "transparent",
                        color: "#94a3b8", fontSize: 14, cursor: "pointer",
                        padding: 0, lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable list area — All Staff always visible as the
                  reset option, then the filtered roster. */}
              <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
              {/* All Staff option */}
              <div
                onClick={() => {
                  setSelectedStaff({ id: "all", name: "All Staff" });
                  setStaffDropdownOpen(false);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "11px 14px", cursor: "pointer",
                  background: selectedStaff.id === "all" ? "#eff6ff" : "#fff",
                  borderBottom: "1px solid #f1f5f9",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  if (selectedStaff.id !== "all") (e.currentTarget as HTMLDivElement).style.background = "#f8fafc";
                }}
                onMouseLeave={(e) => {
                  if (selectedStaff.id !== "all") (e.currentTarget as HTMLDivElement).style.background = "#fff";
                }}
              >
                <div
                  style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: "#e0e7ff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16,
                  }}
                >
                  👥
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13, fontWeight: 600,
                      color: selectedStaff.id === "all" ? "#2563eb" : "#1a1a2e",
                    }}
                  >
                    All Staff
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>Show all sessions</div>
                </div>
                {selectedStaff.id === "all" && (
                  <span style={{ marginLeft: "auto", color: "#2563eb", fontSize: 16 }}>✓</span>
                )}
              </div>

              {/* Individual staff options — filtered by the sticky search
                  input. Case-insensitive substring match against the full
                  displayed name. Empty query returns everything. */}
              {(() => {
                const q = staffSearch.trim().toLowerCase();
                const filtered = q
                  ? staffList.filter((s) => s.name.toLowerCase().includes(q))
                  : staffList;
                if (filtered.length === 0 && q) {
                  return (
                    <div
                      style={{
                        padding: "16px 14px",
                        fontSize: 12.5, color: "#94a3b8",
                        textAlign: "center",
                      }}
                    >
                      No staff found
                    </div>
                  );
                }
                return filtered.map((staff) => {
                const initials = staff.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                const isSelected = selectedStaff.id === staff.id;
                // Consistent color per name — same hash as elsewhere in the app.
                const colors = ["#1b3a6b", "#7c3aed", "#0891b2", "#16a34a", "#dc2626", "#d97706"];
                const color = colors[(staff.name.charCodeAt(0) || 0) % colors.length];
                return (
                  <div
                    key={staff.id}
                    onClick={() => {
                      setSelectedStaff({ id: staff.id, name: staff.name });
                      setStaffDropdownOpen(false);
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "11px 14px", cursor: "pointer",
                      background: isSelected ? "#eff6ff" : "#fff",
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "#fff";
                    }}
                  >
                    <div
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 700, color: "#fff",
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13, fontWeight: 600,
                          color: isSelected ? "#2563eb" : "#1a1a2e",
                        }}
                      >
                        {staff.name}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>Teacher</div>
                    </div>
                    {isSelected && <span style={{ color: "#2563eb", fontSize: 16 }}>✓</span>}
                  </div>
                );
              });
              })()}
              </div>
            </div>
          )}
          <style>{`@keyframes staffDropdownFade{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}`}</style>
        </div>
        )}

        <button
          onClick={() => refetch()}
          style={{
            marginLeft: "auto", background: "none",
            color: "#94a3b8", border: "1px solid #e2e8f0",
            borderRadius: 9, padding: "7px 12px",
            fontSize: 12, cursor: "pointer",
          }}
        >
          ⟳ Refresh
        </button>
      </div>

      {/* Sessions list */}
      {isFetching ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: 14, padding: 48,
          textAlign: "center", border: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, color: "#1b3a6b", fontSize: 16 }}>
            No sessions found for {rangeLabel[rangeKey].toLowerCase()}
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>
            Try a wider range or check back later.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(() => {
            // Tab classification (2026-08-23):
            //   Upcoming & Today — every session in the selected date
            //     range regardless of attendance state. A submitted
            //     session that falls inside Today / This Week / This
            //     Month / All remains visible here so the operational
            //     calendar view stays complete. Row action
            //     (Mark vs. View Report) is derived per-session below
            //     from `attendance_submitted`, not from the tab.
            //   Past Records — every submitted session in the range
            //     (dedicated attendance-history workspace). A future
            //     session that has no submission yet stays out.
            //
            // Both tabs are NON-EXCLUSIVE datasets: a Mon-Aug-24 row
            // marked submitted appears in BOTH tabs when "This Week"
            // is selected.
            const filtered = sessions.filter((s) =>
              activeTab === "past"
                ? !!s.attendance_submitted
                : true
            );
            filtered.sort((a, b) =>
              activeTab === "upcoming"
                ? a.date.localeCompare(b.date)
                : b.date.localeCompare(a.date)
            );
            if (filtered.length === 0) {
              return (
                <div style={{
                  background: "#fff", borderRadius: 14, padding: "40px 24px",
                  border: "1px dashed #e2e8f0", textAlign: "center",
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{activeTab === "upcoming" ? "📅" : "📋"}</div>
                  <div style={{ fontWeight: 700, color: "#334155", fontSize: 14 }}>
                    {activeTab === "upcoming"
                      ? "No upcoming sessions in this range."
                      : "No past sessions in this range."}
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    Try widening the range or switching tabs.
                  </div>
                </div>
              );
            }
            return filtered.map((session) => {
              // Card layout (spec-aligned):
              //   • Single flex row: left color bar · main content · right column
              //   • Main content stacks: class name → date pill + time
              //     → staff/grade/subject/location/students → optional result chips
              //   • Right column is fixed-width so the date NEVER
              //     floats into it (that was the visual bug we hit).
              const isPast = session.date < todayStr;

              // Batch 16 — a session whose scheduled end has passed with
              // no actual_start_at recorded is Not Started, not "Upcoming"
              // (the old branch missed this). Uses the parseDateOnly path
              // to anchor session.date at local midnight before adding
              // end_time, so a same-day session close to midnight is
              // classified against the correct wall-clock.
              const endMs = (() => {
                if (!session.date || !session.end_time) return 0;
                const d = parseDateOnly(session.date);
                if (!d) return 0;
                const [h, m] = session.end_time.split(":").map(Number);
                d.setHours(h || 0, m || 0, 0, 0);
                return d.getTime();
              })();
              const scheduledEndPassed = endMs > 0 && Date.now() > endMs;
              const notStarted = !session.is_cancelled
                && !session.attendance_started
                && !session.attendance_submitted
                && scheduledEndPassed;

              const barColor = session.is_cancelled
                ? "#94a3b8"
                : session.attendance_submitted ? "#16a34a"
                : notStarted ? "#ea580c"
                : isPast ? "#94a3b8"
                : "#1b3a6b";

              // Right-column status pill — 4 distinct states plus Not Started.
              const statusChip = session.is_cancelled
                ? { label: "❌ Cancelled", bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" }
                : session.attendance_submitted
                ? { label: "✅ Submitted",    bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" }
                : notStarted
                ? { label: "🚨 Not Started", bg: "rgba(234,88,12,0.12)", color: "#9a3412", border: "rgba(234,88,12,0.35)" }
                : isPast
                ? { label: "⚠️ Not Submitted", bg: "#fef2f2", color: "#dc2626", border: "#fecaca" }
                : session.attendance_started
                ? { label: "🔄 In Progress",  bg: "#fffbeb", color: "#d97706", border: "#fde68a" }
                : { label: "⏳ Upcoming",     bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };

              const goesToReport = isPast || session.attendance_submitted;
              const buttonLabel = goesToReport ? "📊 View Report" : "✏️ Mark Attendance";
              const buttonAction = () => navigate(
                goesToReport
                  ? `/attendance/report/${session.id}`
                  : `/class-sessions/${session.id}/attendance`
              );

              return (
                <div
                  key={session.id}
                  style={{
                    background: "#fff", borderRadius: 14,
                    border: session.attendance_submitted
                      ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                    display: "flex", alignItems: "stretch", gap: 0,
                  }}
                >
                  {/* Left color bar */}
                  <div style={{
                    width: 5, alignSelf: "stretch", flexShrink: 0,
                    background: barColor,
                  }} />

                  {/* MAIN CONTENT (grows) */}
                  <div style={{ flex: 1, minWidth: 0, padding: "16px 20px" }}>
                    {/* Row 1 — Subjects by Grade.
                        Small cases (1 grade, ≤3 subjects) render the full
                        one-liner directly since a modal would be overkill.
                        Larger cases render a compact summary
                        "Grades 2–7 · 18 subject mappings" + a View Subjects
                        button that opens the grouped modal. Same source of
                        truth in both branches — the occurrence-narrowed
                        grade_subjects payload. */}
                    {(() => {
                      const gs = Array.isArray(session.grade_subjects) ? session.grade_subjects : [];
                      if (gs.length === 0) {
                        return (
                          <div style={{ fontWeight: 700, fontSize: 16, color: "#1b3a6b", marginBottom: 8 }}>
                            {session.class_name}
                          </div>
                        );
                      }
                      const totalSubjects = gs.reduce((n, g) => n + g.subjects.length, 0);
                      const simple = gs.length === 1 && totalSubjects <= 3;
                      if (simple) {
                        return (
                          <div style={{ marginBottom: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
                            <span style={{ fontSize: 16, marginTop: 1 }}>📚</span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <GradeSubjectsSummary groups={gs} variant="light" />
                            </div>
                          </div>
                        );
                      }
                      const { gradesLabel, mappingsCount } = summarizeGradesCompact(gs);
                      const subtitleParts = [
                        session.staff_name,
                        formatDateShort(session.date),
                        `${formatTime(session.start_time)} – ${formatTime(session.end_time)}`,
                      ].filter(Boolean);
                      return (
                        <div style={{
                          marginBottom: 8, display: "flex", alignItems: "center",
                          gap: 10, flexWrap: "wrap",
                        }}>
                          <span style={{ fontSize: 16 }}>📚</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: "#1b3a6b", lineHeight: 1.3 }}>
                              {gradesLabel}
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                              {mappingsCount} subject mapping{mappingsCount === 1 ? "" : "s"}
                            </div>
                          </div>
                          <button
                            onClick={() => setSubjectsModal({ groups: gs, subtitle: subtitleParts.join(" · ") })}
                            style={{
                              background: "#eff6ff", color: "#1b3a6b",
                              border: "1px solid #dbeafe", borderRadius: 8,
                              padding: "6px 12px", fontSize: 12, fontWeight: 700,
                              cursor: "pointer", whiteSpace: "nowrap",
                            }}
                            aria-label="View subjects for this class"
                          >
                            📚 View Subjects
                          </button>
                        </div>
                      );
                    })()}

                    {/* Row 2 — Date pill + time (spec §1: date lives
                        INSIDE the card next to the time; no longer
                        floats in the right column) */}
                    <div style={{
                      display: "flex", alignItems: "center",
                      gap: 10, marginBottom: 8, flexWrap: "wrap",
                    }}>
                      <span style={{
                        background: "#1b3a6b", color: "#fff",
                        borderRadius: 8, padding: "4px 12px",
                        fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
                      }}>
                        📅 {formatDateShort(session.date)}
                      </span>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
                        🕐 {formatTime(session.start_time)} – {formatTime(session.end_time)}
                      </span>
                      {/* Batch 16 — midnight-boundary hint. A class starting
                          before ~5 AM is easy to misread as "last night";
                          call it out plainly so a teacher looking for
                          "tonight's midnight class" knows to look on the
                          date it starts, not the evening before. */}
                      {session.start_time && session.start_time < "05:00" && (
                        <span style={{
                          fontSize: 11, color: "#9a3412",
                          background: "rgba(234,88,12,0.10)",
                          border: "1px solid rgba(234,88,12,0.25)",
                          borderRadius: 6, padding: "1px 8px", fontWeight: 600,
                        }}>
                          🌙 overnight — starts after midnight
                        </span>
                      )}
                    </div>

                    {/* Row 3 — Staff avatar + name · grade · subject · location · students */}
                    <div style={{
                      display: "flex", alignItems: "center",
                      gap: 10, flexWrap: "wrap",
                    }}>
                      {session.staff_name && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <img
                            src={getAvatar(session.staff_name, session.staff_photo, 56)}
                            alt=""
                            style={{
                              width: 28, height: 28, borderRadius: "50%",
                              objectFit: "cover", border: "2px solid #e2e8f0",
                            }}
                            onError={(e) => {
                              const t = e.currentTarget as HTMLImageElement;
                              // Break the loop if the fallback itself errors.
                              t.onerror = null;
                              t.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(session.staff_name || "?")}&background=1b3a6b&color=fff&size=56&rounded=true&bold=true`;
                            }}
                          />
                          <span style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>
                            {session.staff_name}
                          </span>
                        </div>
                      )}
                      {/* Grade + flat subject chips removed: Subjects by
                          Grade above (GradeSubjectsSummary) is the single
                          source of truth for what the class is. The old
                          blue "2, 3, 4, 5" chip and green "English and
                          Math, English and Science, …" chip duplicated
                          each other without pairing which subject to
                          which grade — confusing on multi-grade rows. */}
                      {session.location && (
                        <>
                          {session.staff_name && <span style={{ color: "#e2e8f0" }}>·</span>}
                          <span style={{ fontSize: 12, color: "#64748b" }}>📍 {session.location}</span>
                        </>
                      )}
                      {(session.staff_name || session.location) && <span style={{ color: "#e2e8f0" }}>·</span>}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        👥 {session.student_count} students
                      </span>
                    </div>

                    {/* Row 4 — Attendance result chips (only when marked) */}
                    {session.attendance_submitted && (session.present_count + session.absent_count + session.late_count) > 0 && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                        {session.present_count > 0 && (
                          <span style={{
                            background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
                            borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                          }}>✅ Present: {session.present_count}</span>
                        )}
                        {session.absent_count > 0 && (
                          <span style={{
                            background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                            borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                          }}>❌ Absent: {session.absent_count}</span>
                        )}
                        {session.late_count > 0 && (
                          <span style={{
                            background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a",
                            borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                          }}>🕐 Late: {session.late_count}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RIGHT column: fixed width, status + button.
                      minWidth=160 stops it from squeezing into a
                      tiny slot on narrow viewports; borderLeft
                      visually separates the actions area. */}
                  <div style={{
                    padding: "16px 20px",
                    display: "flex", flexDirection: "column",
                    alignItems: "flex-end", gap: 10,
                    flexShrink: 0, minWidth: 160,
                    borderLeft: "1px solid #f1f5f9",
                  }}>
                    <span style={{
                      borderRadius: 8, padding: "5px 12px",
                      fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                      background: statusChip.bg, color: statusChip.color,
                      border: `1px solid ${statusChip.border}`,
                    }}>
                      {statusChip.label}
                    </span>
                    <button
                      onClick={buttonAction}
                      disabled={session.is_cancelled}
                      style={{
                        background: session.is_cancelled
                          ? "#f1f5f9"
                          : goesToReport ? "#fff" : "#1b3a6b",
                        color: session.is_cancelled
                          ? "#94a3b8"
                          : goesToReport ? "#1b3a6b" : "#fff",
                        border: goesToReport ? "2px solid #1b3a6b" : "none",
                        borderRadius: 10, padding: "10px 18px",
                        fontWeight: 700, fontSize: 13,
                        cursor: session.is_cancelled ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        boxShadow: goesToReport || session.is_cancelled
                          ? "none" : "0 4px 12px rgba(27,58,107,0.3)",
                      }}
                    >
                      {session.is_cancelled ? "Cancelled" : buttonLabel}
                    </button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
      <GradeSubjectsDialog
        open={!!subjectsModal}
        onClose={() => setSubjectsModal(null)}
        groups={subjectsModal?.groups ?? []}
        subtitle={subjectsModal?.subtitle}
      />
    </div>
  );
};

export default AttendanceLanding;
